import { ArrowLeftIcon } from '@heroicons/react/20/solid';
import { Link } from 'react-router-dom';
import { DESTINATION_BY_ID } from '@/app/config/destinations';
import type { DestinationCapabilityDefinition } from './destinationCapabilityTypes';

interface DestinationCapabilityFrameProps {
  capability: DestinationCapabilityDefinition;
}

/**
 * Keeps an absorbed legacy function inside its owning product destination.
 * The existing implementation remains intact, but it no longer presents as a
 * sixth top-level surface or competes with destination navigation.
 */
export function DestinationCapabilityFrame({ capability }: DestinationCapabilityFrameProps) {
  const destination = DESTINATION_BY_ID[capability.destination];

  return (
    <section
      className="min-w-0"
      data-surface="destination-capability"
      data-destination={capability.destination}
      data-capability-path={capability.capabilityPath}
    >
      <nav
        aria-label={`${destination.label} capability context`}
        className="mb-4 flex min-w-0 items-center gap-2 rounded-xl border border-[rgb(var(--color-border-primary)/0.55)] bg-[rgb(var(--color-bg-secondary)/0.7)] px-3 py-2 text-sm"
      >
        <Link
          to={destination.landingUrl}
          className="inline-flex shrink-0 items-center gap-1.5 font-medium text-[rgb(var(--color-text-secondary))] transition-colors hover:text-[rgb(var(--color-text-primary))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-ring"
        >
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
          {destination.label}
        </Link>
        <span aria-hidden="true" className="text-[rgb(var(--color-text-tertiary))]">
          /
        </span>
        <span
          className="min-w-0 truncate font-semibold text-[rgb(var(--color-text-primary))]"
          aria-current="page"
        >
          {capability.label}
        </span>
      </nav>
      <div key={capability.capabilityPath} className="min-w-0">
        {capability.element}
      </div>
    </section>
  );
}
